import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import Info from '@mui/icons-material/InfoOutline';
import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { AxiosError } from 'axios';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { formValidationSchema, type RegisterEmployerFormData } from './schema';
import { fontWeights } from '@/@mui/tokens/font-weights';
import { AutocompleteStatesAndCities } from '@/components/partial/autocomplete-states-cities';
import { ContentBox } from '@/components/shared/content-box';
import { MaskedInput } from '@/components/shared/masked-input';
import { PageContainer } from '@/components/shared/page-container';
import { PageHeader } from '@/components/shared/page-header';
import { useNotification } from '@/context/notification';
import { useEditEmployer, useEmployeeShow } from '@/data/employees';

export const EditEmployer = () => {
  const navigate = useNavigate();
  const { toast } = useNotification();
  const { id } = useParams();
  const { mutate: editEmployer, isPending } = useEditEmployer();
  const { employee, isAdmin } = useEmployeeShow({ id: Number(id) });
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [selectedState, setSelectedState] = useState<string>('');

  const { control, handleSubmit, reset } = useForm<RegisterEmployerFormData>({
    resolver: zodResolver(formValidationSchema),
  });

  useEffect(() => {
    if (employee) {
      reset({
        email: employee.email || '',
        cpf: employee.cpf || '',
        name: employee.nome || '',
        type_registration: employee.cpf ? 'cpf' : 'email',
        is_admin: isAdmin || false,
        internal_employee_id:
          employee.colaborador?.corporate_data?.internal_employee_id || '',
        type_collaborator:
          employee.colaborador?.corporate_data?.type_collaborator || '',
        position_company:
          employee.colaborador?.corporate_data?.position_company || '',
        team: employee.colaborador?.corporate_data?.team || '',
        department: employee.colaborador?.corporate_data?.department || '',
        cost_center: employee.colaborador?.corporate_data?.cost_center || '',
        manager_email:
          employee.colaborador?.corporate_data?.manager_email || '',
        company: employee.colaborador?.corporate_data?.company || '',
        region: employee.colaborador?.corporate_data?.region || '',
        building: employee.colaborador?.corporate_data?.building || '',
        state: employee.colaborador?.corporate_data?.state || '',
        city: employee.colaborador?.corporate_data?.city || '',
      });
    }
  }, [employee, isAdmin, reset]);

  const handleRegisterEmployer = (data: RegisterEmployerFormData) => {
    editEmployer(
      {
        id: Number(id),
        data: {
          name: data.name,
          email: data.email || '',
          cpf: data.cpf || '',
          is_admin: data.is_admin || false,
          type_registration: data.type_registration,
          corporate_data: {
            internal_employee_id: data.internal_employee_id,
            type_collaborator: data.type_collaborator,
            position_company: data.position_company,
            team: data.team,
            department: data.department,
            cost_center: data.cost_center,
            manager_email: data.manager_email,
            company: data.company,
            region: data.region,
            building: data.building,
            state: selectedState || data.state,
            city: selectedCity || data.city,
          },
        },
      },
      {
        onSuccess: () => {
          toast({
            content: 'Colaborador atualizado com sucesso!',
            type: 'success',
          });
          navigate('/empresa/gestao-de-colaboradores');
        },
        onError: (error) => {
          toast({
            title: 'Erro ao adicionar colaborador',
            content:
              (error as AxiosError<{ data: { message: string } }>)?.response
                ?.data?.data?.message ||
              'Entre em contato com o suporte ou tente novamente.',
            type: 'error',
          });
        },
      }
    );
  };

  return (
    <PageContainer>
      <PageHeader
        backNavigationText="Lista de colaboradores"
        handleBackNavigation={() =>
          navigate('/empresa/gestao-de-colaboradores')
        }
        title="Adicionar colaborador"
      />
      <ContentBox>
        <Stack mb={2}>
          <FormLabel>Nome Completo*</FormLabel>
          <Controller
            control={control}
            name="name"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                error={!!error}
                placeholder="Insira o nome completo do colaborador"
                helperText={error?.message || ' '}
              />
            )}
          />
        </Stack>

        <Stack direction={{ md: 'row' }} gap={2}>
          <Stack flex={1}>
            <FormLabel>E-mail</FormLabel>
            <Controller
              control={control}
              name="email"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  error={!!error}
                  helperText={error?.message || ' '}
                  placeholder="colaborador@nomeempresa.com"
                />
              )}
            />
          </Stack>

          <Stack flex={1}>
            <FormLabel>CPF</FormLabel>
            <Controller
              control={control}
              name="cpf"
              render={({ field, fieldState: { error } }) => (
                <MaskedInput
                  {...field}
                  maskType="cpf"
                  error={!!error}
                  placeholder="123.456.789-00"
                  helperText={error?.message || ' '}
                />
              )}
            />
          </Stack>
        </Stack>

        <Stack direction="row" alignItems="center" gap={1} my={3}>
          <Typography
            fontWeight={fontWeights.fontWeightSemibold}
            variant="h3"
            color="primary.dark"
          >
            Atributos (opcional)
          </Typography>
          <Tooltip title="Preencher os campos opcionais permite que eles se tornem filtro no relatório de colaboradores.">
            <Info fontSize="small" />
          </Tooltip>
        </Stack>

        <Stack direction={{ md: 'row' }} gap={2}>
          <Stack flex={1}>
            <FormLabel>ID interno</FormLabel>
            <Controller
              control={control}
              name="internal_employee_id"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  error={!!error}
                  fullWidth
                  placeholder="ex.: número de matrícula, chave interna"
                  helperText={error?.message || ' '}
                />
              )}
            />
          </Stack>
          <Stack flex={1}>
            <FormLabel>Tipo de colaborador</FormLabel>
            <Controller
              control={control}
              name="type_collaborator"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  error={!!error}
                  fullWidth
                  placeholder="ex.: CLT, externo, prestador de serviço"
                  helperText={error?.message || ' '}
                />
              )}
            />
          </Stack>
        </Stack>

        <Stack direction={{ md: 'row' }} gap={2}>
          <Stack flex={1}>
            <FormLabel>Cargo</FormLabel>
            <Controller
              control={control}
              name="position_company"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  error={!!error}
                  fullWidth
                  placeholder="ex.: analista, coordenador"
                  helperText={error?.message || ' '}
                />
              )}
            />
          </Stack>
          <Stack flex={1}>
            <FormLabel>Equipe</FormLabel>
            <Controller
              control={control}
              name="team"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  error={!!error}
                  fullWidth
                  placeholder="ex.: atração de talentos, desenvolvimento"
                  helperText={error?.message || ' '}
                />
              )}
            />
          </Stack>
        </Stack>

        <Stack direction={{ md: 'row' }} gap={2}>
          <Stack flex={1}>
            <FormLabel>Departamento</FormLabel>
            <Controller
              control={control}
              name="department"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  error={!!error}
                  fullWidth
                  placeholder="ex.: recursos humanos, marketing"
                  helperText={error?.message || ' '}
                />
              )}
            />
          </Stack>
          <Stack flex={1}>
            <FormLabel>Centro de custo</FormLabel>
            <Controller
              control={control}
              name="cost_center"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  error={!!error}
                  fullWidth
                  placeholder="ex.: produtos digitais"
                  helperText={error?.message || ' '}
                />
              )}
            />
          </Stack>
        </Stack>

        <Stack direction={{ md: 'row' }} gap={2}>
          <Stack flex={1}>
            <FormLabel>Gestor</FormLabel>
            <Controller
              control={control}
              name="manager_email"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  error={!!error}
                  fullWidth
                  placeholder="Insira o email"
                  helperText={error?.message || ' '}
                />
              )}
            />
          </Stack>
          <Stack flex={1}>
            <FormLabel sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Empresa
              <Tooltip title="Campo para holding que possuem diversas empresas.">
                <Info fontSize="small" />
              </Tooltip>
            </FormLabel>
            <Controller
              control={control}
              name="company"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  error={!!error}
                  fullWidth
                  placeholder="Insira o email"
                  helperText={error?.message || ' '}
                />
              )}
            />
          </Stack>
        </Stack>
        <AutocompleteStatesAndCities
          onStateAndCityChange={(state, city) => {
            setSelectedState(state);
            setSelectedCity(city);
          }}
          initialState={
            selectedState || employee?.colaborador?.corporate_data?.state || ''
          }
          initialCity={
            selectedCity || employee?.colaborador?.corporate_data?.city || ''
          }
        />
        <Stack direction={{ md: 'row' }} gap={2}>
          <Stack flex={1}>
            <FormLabel>Região</FormLabel>
            <Controller
              control={control}
              name="region"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  error={!!error}
                  fullWidth
                  placeholder="ex.: filial, região de atuação"
                  helperText={error?.message || ' '}
                />
              )}
            />
          </Stack>
          <Stack flex={1}>
            <FormLabel>Prédio</FormLabel>
            <Controller
              control={control}
              name="building"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  error={!!error}
                  fullWidth
                  placeholder="ex.: nome do edifício, campus ou andar"
                  helperText={error?.message || ' '}
                />
              )}
            />
          </Stack>
        </Stack>
        <Stack justifyContent="start">
          <Controller
            name="is_admin"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <FormControlLabel
                sx={{ width: 'fit-content' }}
                control={
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={
                  <Stack direction="row" alignItems="center" gap={2}>
                    <Typography fontWeight={fontWeights.fontWeightSemibold}>
                      Administrador
                    </Typography>
                    <Tooltip title="Dá acesso à gestão de usuários, relatórios e outras configurações da plataforma.">
                      <Info fontSize="small" />
                    </Tooltip>
                  </Stack>
                }
              />
            )}
          />
        </Stack>
        <Divider sx={{ my: 3 }} />
        <Stack direction="row" justifyContent="flex-end" gap={2}>
          <Button
            variant="outlined"
            onClick={() => navigate('/empresa/gestao-de-colaboradores')}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            loading={isPending}
            onClick={handleSubmit(handleRegisterEmployer)}
          >
            Salvar
          </Button>
        </Stack>
      </ContentBox>
    </PageContainer>
  );
};
